import { Edge, Node, ViewportPortal, useReactFlow } from "@xyflow/react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { isEditableElement } from "../../hooks/UseShortcutsHook";
import { SequenceTextbox } from "../../models/interactions/Sequence";
import { SequenceNodeData } from "./SequenceNode";
import { CopyPasteContext } from "../training_timeline/CopyPasteContext";
import {
  getSelectionInputMode,
  SequenceCanvasItem,
  SelectionInputMode,
} from "./SequenceCanvasSelection.hook";

const DEFAULT_TEXTBOX_WIDTH = 280;
const DEFAULT_TEXTBOX_HEIGHT = 56;
const MIN_TEXTBOX_WIDTH = 180;
const MIN_TEXTBOX_HEIGHT = 56;
const TEXTBOX_EDGE_HANDLE_SIZE = 8;
const TEXTBOX_CORNER_HANDLE_SIZE = 12;
const DEFAULT_TEXTBOX_COLOR = "#3AC4FF";

function withAlpha(hexColor: string, alphaHex: string) {
  const normalized = hexColor.trim();
  if (!normalized.startsWith("#")) {
    return hexColor;
  }

  if (normalized.length === 4) {
    const r = normalized[1];
    const g = normalized[2];
    const b = normalized[3];
    return `#${r}${r}${g}${g}${b}${b}${alphaHex}`;
  }

  if (normalized.length === 7) {
    return `${normalized}${alphaHex}`;
  }

  return hexColor;
}

export function createSequenceTextbox(position: {
  x: number;
  y: number;
}): SequenceTextbox {
  return {
    id: `textbox-${GetNewGuid()}`,
    position,
    size: {
      width: DEFAULT_TEXTBOX_WIDTH,
      height: DEFAULT_TEXTBOX_HEIGHT,
    },
    text: "",
  };
}

type PointerMode = "move" | "resize-right" | "resize-bottom" | "resize-corner";

type DragState = {
  textboxId: string;
  pointerId: number;
  mode: PointerMode;
  startFlowPoint: { x: number; y: number };
  initialTextbox: SequenceTextbox;
  previewTextboxes: SequenceTextbox[];
};

type SequenceTextboxLayerProps = {
  sequenceId?: string;
  textboxes: SequenceTextbox[];
  selectedTextboxIds: string[];
  onSelectItem: (
    item: SequenceCanvasItem,
    mode: SelectionInputMode,
    options?: { preserveIfSelected?: boolean },
  ) => void;
  onCreateTextbox: (textbox: SequenceTextbox) => void;
  onDeleteTextboxes: (textboxIds: string[]) => void;
  onUpdateTextbox: (
    textboxId: string,
    updates: Partial<SequenceTextbox>,
    persist?: boolean,
  ) => void;
  setLocalTextboxes: (
    updater:
      SequenceTextbox[] | ((current: SequenceTextbox[]) => SequenceTextbox[]),
  ) => void;
  persistCanvasState: (params: { nextTextboxes?: SequenceTextbox[] }) => void;
};

export default function SequenceTextboxLayer({
  sequenceId = "",
  textboxes = [],
  selectedTextboxIds = [],
  onSelectItem,
  onCreateTextbox,
  onDeleteTextboxes,
  onUpdateTextbox,
  setLocalTextboxes,
  persistCanvasState,
}: SequenceTextboxLayerProps) {
  const copyPasteContext = useContext(CopyPasteContext);
  const pendingSelection =
    copyPasteContext?.pendingCut?.kind === "sequenceSelection" &&
    copyPasteContext.pendingCut.source.sequenceId === sequenceId
      ? copyPasteContext.pendingCut.selection
      : null;
  const { screenToFlowPosition, getViewport } = useReactFlow<
    Node<SequenceNodeData>,
    Edge
  >();
  const dragStateRef = useRef<DragState | null>(null);
  const lastPointerScreenRef = useRef<{ x: number; y: number } | null>(null);
  const textareaRefs = useRef(new Map<string, HTMLTextAreaElement | null>());
  const [hoveredTextboxId, setHoveredTextboxId] = useState<string | null>(null);
  const [editingTextboxId, setEditingTextboxId] = useState<string | null>(null);

  const selectedTextboxIdSet = useMemo(
    () => new Set(selectedTextboxIds),
    [selectedTextboxIds],
  );

  const getTextboxContentHeight = (textarea: HTMLTextAreaElement) => {
    const previousHeight = textarea.style.height;
    textarea.style.height = "0px";
    const contentHeight = textarea.scrollHeight;
    textarea.style.height = previousHeight;
    return contentHeight;
  };

  const getExpandedTextboxHeight = (
    textbox: SequenceTextbox,
    textarea: HTMLTextAreaElement,
  ) => {
    const nextHeight = Math.max(
      MIN_TEXTBOX_HEIGHT,
      getTextboxContentHeight(textarea),
      textbox.size.height,
    );

    return nextHeight;
  };

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      lastPointerScreenRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
    };

    window.addEventListener("pointermove", handlePointerMove);
    return () => window.removeEventListener("pointermove", handlePointerMove);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() !== "t" ||
        !event.altKey ||
        isEditableElement(event.target)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      let position = { x: 120, y: 120 };
      if (lastPointerScreenRef.current) {
        position = screenToFlowPosition(lastPointerScreenRef.current);
      } else {
        const bounds = document
          .querySelector(".react-flow")
          ?.getBoundingClientRect();
        const viewport = getViewport();
        if (bounds) {
          const centerPoint = screenToFlowPosition({
            x: bounds.left + bounds.width / 2,
            y: bounds.top + bounds.height / 2,
          });
          position = {
            x: centerPoint.x - DEFAULT_TEXTBOX_WIDTH / 2,
            y: centerPoint.y - DEFAULT_TEXTBOX_HEIGHT / 2,
          };
        } else {
          position = {
            x: -viewport.x + 80,
            y: -viewport.y + 80,
          };
        }
      }

      const textbox = createSequenceTextbox(position);

      onCreateTextbox(textbox);
      onSelectItem({ kind: "textbox", id: textbox.id }, "replace");
      setEditingTextboxId(textbox.id);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [getViewport, onCreateTextbox, onSelectItem, screenToFlowPosition]);

  useEffect(() => {
    if (!editingTextboxId) {
      return;
    }

    const textarea = textareaRefs.current.get(editingTextboxId);
    if (!textarea) {
      return;
    }

    textarea.focus();
    const textLength = textarea.value.length;
    textarea.setSelectionRange(textLength, textLength);
  }, [editingTextboxId]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      const currentFlowPoint = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const deltaX = currentFlowPoint.x - dragState.startFlowPoint.x;
      const deltaY = currentFlowPoint.y - dragState.startFlowPoint.y;

      const nextTextboxes = textboxes.map((textbox) => {
        if (textbox.id !== dragState.textboxId) {
          return textbox;
        }

        if (dragState.mode === "move") {
          return {
            ...textbox,
            position: {
              x: dragState.initialTextbox.position.x + deltaX,
              y: dragState.initialTextbox.position.y + deltaY,
            },
          };
        }

        if (dragState.mode === "resize-right") {
          return {
            ...textbox,
            size: {
              width: Math.max(
                MIN_TEXTBOX_WIDTH,
                dragState.initialTextbox.size.width + deltaX,
              ),
              height: dragState.initialTextbox.size.height,
            },
          };
        }

        if (dragState.mode === "resize-bottom") {
          return {
            ...textbox,
            size: {
              width: dragState.initialTextbox.size.width,
              height: Math.max(
                MIN_TEXTBOX_HEIGHT,
                dragState.initialTextbox.size.height + deltaY,
              ),
            },
          };
        }

        return {
          ...textbox,
          size: {
            width: Math.max(
              MIN_TEXTBOX_WIDTH,
              dragState.initialTextbox.size.width + deltaX,
            ),
            height: Math.max(
              MIN_TEXTBOX_HEIGHT,
              dragState.initialTextbox.size.height + deltaY,
            ),
          },
        };
      });

      dragState.previewTextboxes = nextTextboxes;
      setLocalTextboxes(nextTextboxes);
    };

    const handlePointerUp = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      dragStateRef.current = null;
      persistCanvasState({ nextTextboxes: dragState.previewTextboxes });
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [persistCanvasState, screenToFlowPosition, setLocalTextboxes, textboxes]);

  const startTextboxPointerInteraction =
    (textbox: SequenceTextbox, mode: PointerMode) =>
    (event: React.PointerEvent<HTMLDivElement>) => {
      if ((event.target as HTMLElement).closest("textarea")) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragStateRef.current = {
        textboxId: textbox.id,
        pointerId: event.pointerId,
        mode,
        startFlowPoint: screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        }),
        initialTextbox: {
          ...textbox,
          position: { ...textbox.position },
          size: { ...textbox.size },
        },
        previewTextboxes: textboxes,
      };
      onSelectItem(
        { kind: "textbox", id: textbox.id },
        getSelectionInputMode(event),
        { preserveIfSelected: true },
      );
      setEditingTextboxId(null);
    };

  return (
    <ViewportPortal>
      {textboxes.map((textbox) => {
        const isPendingCut = Boolean(
          pendingSelection?.textboxIds.includes(textbox.id),
        );
        const selected = selectedTextboxIdSet.has(textbox.id);
        const editing = editingTextboxId === textbox.id;
        const hovered = hoveredTextboxId === textbox.id;
        const showChrome = selected || editing || hovered;
        const accentColor = textbox.color ?? DEFAULT_TEXTBOX_COLOR;
        const borderColor = showChrome
          ? textbox.color
            ? accentColor
            : "rgba(255,255,255,0.22)"
          : "transparent";
        const backgroundColor = showChrome
          ? textbox.color
            ? withAlpha(accentColor, selected || editing ? "22" : "14")
            : "rgba(17, 24, 39, 0.28)"
          : "transparent";
        const boxShadow = selected
          ? textbox.color
            ? `0 0 0 1px ${accentColor}55, 0 4px 18px ${accentColor}30`
            : "0 0 0 1px rgba(58,196,255,0.45)"
          : undefined;
        const textColor = textbox.color ?? "var(--creator-text-primary)";
        const placeholderColor = textbox.color
          ? withAlpha(accentColor, "AA")
          : "var(--creator-text-sub)";

        return (
          <div
            key={textbox.id}
            className={`absolute transition-opacity duration-150 motion-reduce:transition-none ${
              isPendingCut ? "opacity-60" : ""
            }`}
            style={{
              left: textbox.position.x,
              top: textbox.position.y,
              width: textbox.size.width,
              height: textbox.size.height,
              zIndex: 4,
              pointerEvents: "auto",
            }}
            data-testid={`sequence-textbox-${textbox.id}`}
            role="group"
            aria-selected={selected}
            aria-label={
              isPendingCut
                ? "Cut pending. Paste to move or press Escape to cancel."
                : undefined
            }
            onMouseEnter={() => setHoveredTextboxId(textbox.id)}
            onMouseLeave={() =>
              setHoveredTextboxId((current) =>
                current === textbox.id ? null : current,
              )
            }
            onPointerDown={(event) => {
              if ((event.target as HTMLElement).closest("textarea")) {
                return;
              }
              event.stopPropagation();
              onSelectItem(
                { kind: "textbox", id: textbox.id },
                getSelectionInputMode(event),
                { preserveIfSelected: true },
              );
            }}
            onDoubleClick={() => {
              onSelectItem({ kind: "textbox", id: textbox.id }, "replace");
              setEditingTextboxId(textbox.id);
            }}
          >
            {isPendingCut && (
              <span
                className="pointer-events-none absolute -top-3 right-2 z-20 flex h-6 w-6 items-center justify-center rounded-[6px] border border-creator-primary bg-creator-surface text-[16px] text-creator-text-main"
                aria-label="Cut pending. Paste to move or press Escape to cancel."
              >
                <ContentCutOutlinedIcon fontSize="inherit" aria-hidden="true" />
              </span>
            )}
            <div
              className="absolute inset-0 rounded-[10px]"
              style={{
                border: `1px solid ${borderColor}`,
                backgroundColor,
                boxShadow,
              }}
            />
            {editing ? (
              <textarea
                ref={(element) => {
                  textareaRefs.current.set(textbox.id, element);
                }}
                className="absolute left-0 top-0 min-h-full w-full resize-none rounded-[10px] bg-transparent px-4 py-3 text-[22px] font-medium leading-8 text-creator-text-primary outline-none placeholder:text-creator-text-sub"
                style={{
                  pointerEvents: "auto",
                  height: "auto",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  overflow: "hidden",
                  color: textColor,
                }}
                placeholder="Type note"
                value={textbox.text}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  onSelectItem({ kind: "textbox", id: textbox.id }, "replace");
                }}
                onFocus={() => {
                  onSelectItem({ kind: "textbox", id: textbox.id }, "replace");
                  setEditingTextboxId(textbox.id);
                }}
                onKeyDown={(event) => {
                  if (
                    event.key !== "Enter" ||
                    event.shiftKey ||
                    event.ctrlKey ||
                    event.metaKey ||
                    event.altKey
                  ) {
                    return;
                  }

                  event.preventDefault();
                  event.currentTarget.blur();
                }}
                onBlur={(event) => {
                  const nextHeight = getExpandedTextboxHeight(
                    textbox,
                    event.currentTarget,
                  );
                  onUpdateTextbox(
                    textbox.id,
                    {
                      text: event.target.value,
                      size: {
                        ...textbox.size,
                        height: nextHeight,
                      },
                    },
                    true,
                  );
                  setEditingTextboxId((current) =>
                    current === textbox.id ? null : current,
                  );
                }}
                onChange={(event) => {
                  const nextHeight = getExpandedTextboxHeight(
                    textbox,
                    event.currentTarget,
                  );
                  onUpdateTextbox(
                    textbox.id,
                    {
                      text: event.target.value,
                      ...(nextHeight !== textbox.size.height
                        ? {
                            size: {
                              ...textbox.size,
                              height: nextHeight,
                            },
                          }
                        : {}),
                    },
                    false,
                  );
                }}
              />
            ) : (
              <div
                className="absolute inset-0 cursor-move rounded-[10px] px-4 py-3 text-[22px] font-medium leading-8"
                style={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  color: textColor,
                }}
                data-testid={`sequence-textbox-drag-${textbox.id}`}
                onPointerDown={startTextboxPointerInteraction(textbox, "move")}
              >
                {textbox.text || (
                  <span style={{ color: placeholderColor }}>Type note</span>
                )}
              </div>
            )}
            {showChrome && (
              <>
                <div
                  className="absolute top-4 bottom-4 -right-1.5 cursor-ew-resize rounded-full bg-transparent opacity-0"
                  style={{
                    width: TEXTBOX_EDGE_HANDLE_SIZE,
                    pointerEvents: "auto",
                  }}
                  data-testid={`sequence-textbox-resize-right-${textbox.id}`}
                  onPointerDown={startTextboxPointerInteraction(
                    textbox,
                    "resize-right",
                  )}
                />
                <div
                  className="absolute left-4 right-4 -bottom-1.5 cursor-ns-resize rounded-full bg-transparent opacity-0"
                  style={{
                    height: TEXTBOX_EDGE_HANDLE_SIZE,
                    pointerEvents: "auto",
                  }}
                  data-testid={`sequence-textbox-resize-bottom-${textbox.id}`}
                  onPointerDown={startTextboxPointerInteraction(
                    textbox,
                    "resize-bottom",
                  )}
                />
                <div
                  className="absolute -bottom-1.5 -right-1.5 cursor-se-resize rounded-[4px] bg-transparent opacity-0"
                  style={{
                    width: TEXTBOX_CORNER_HANDLE_SIZE,
                    height: TEXTBOX_CORNER_HANDLE_SIZE,
                    pointerEvents: "auto",
                  }}
                  data-testid={`sequence-textbox-resize-corner-${textbox.id}`}
                  onPointerDown={startTextboxPointerInteraction(
                    textbox,
                    "resize-corner",
                  )}
                />
              </>
            )}
          </div>
        );
      })}
    </ViewportPortal>
  );
}
